import React from 'react';

/*
  AnimatedLogo — SVG recreation of the CallEssential waveform logo.
  Bars animate up/down like an audio equalizer, matching the style
  visible on the synergytalk.net website.
*/

interface Props {
  width?: number;
  style?: React.CSSProperties;
  className?: string;
}

// Each bar: x centre, colour, min scale, max scale, animation duration, delay
const BARS = [
  { x: 28,  color: '#d946ef', minS: 0.28, maxS: 0.62, dur: 0.85, delay: 0.00 }, // magenta
  { x: 40,  color: '#a855f7', minS: 0.42, maxS: 0.90, dur: 1.10, delay: 0.18 }, // purple
  { x: 52,  color: '#eab308', minS: 0.20, maxS: 0.50, dur: 0.78, delay: 0.35 }, // gold
  { x: 64,  color: '#facc15', minS: 0.55, maxS: 1.00, dur: 1.20, delay: 0.06 }, // yellow
  { x: 76,  color: '#3b82f6', minS: 0.65, maxS: 1.00, dur: 0.95, delay: 0.50 }, // blue
  { x: 88,  color: '#facc15', minS: 0.48, maxS: 0.92, dur: 1.05, delay: 0.22 }, // yellow
  { x: 100, color: '#06b6d4', minS: 0.32, maxS: 0.72, dur: 1.15, delay: 0.62 }, // cyan
  { x: 112, color: '#22d3ee', minS: 0.18, maxS: 0.44, dur: 0.82, delay: 0.12 }, // light cyan
  { x: 124, color: '#3b82f6', minS: 0.40, maxS: 0.80, dur: 1.00, delay: 0.42 }, // blue
  { x: 136, color: '#d946ef', minS: 0.25, maxS: 0.58, dur: 0.90, delay: 0.28 }, // magenta
];

const VB_W  = 161;  // matches original logo width
const VB_H  = 84;   // matches original logo height
const CY    = VB_H / 2;  // vertical centre
const BAR_W = 8;
const BAR_FULL_H = 64;   // height when scaleY = 1

export default function AnimatedLogo({ width = 160, style, className }: Props) {
  const uid = React.useId().replace(/:/g, '');

  const keyframes = BARS.map((b, i) => `
    @keyframes bar_${uid}_${i} {
      0%,100% { transform: scaleY(${b.minS}); }
      50%      { transform: scaleY(${b.maxS}); }
    }
  `).join('\n');

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width={width}
      height={width * (VB_H / VB_W)}
      xmlns="http://www.w3.org/2000/svg"
      aria-label="CallEssential"
      style={style}
      className={className}
    >
      <style>{keyframes}</style>

      {/* Transparent background */}

      {/* Decorative dots */}
      <circle cx="16" cy="18" r="3.5"  fill="#7c3aed" opacity="0.75" />
      <circle cx="147" cy="16" r="5"   fill="#fb923c" opacity="0.90" />
      <circle cx="139" cy="10" r="2.5" fill="#3b82f6" opacity="0.70" />

      {/* Animated equalizer bars */}
      {BARS.map((b, i) => (
        <rect
          key={i}
          x={b.x - BAR_W / 2}
          y={CY - BAR_FULL_H / 2}
          width={BAR_W}
          height={BAR_FULL_H}
          rx="3"
          fill={b.color}
          style={{
            transformOrigin: `${b.x}px ${CY}px`,
            animation: `bar_${uid}_${i} ${b.dur}s ${b.delay}s infinite ease-in-out`,
          }}
        />
      ))}
    </svg>
  );
}
